import {
    AlphaAttribute,
    ColorAttribute,
    getAttributesToSerialize,
    NumberAttribute
} from '../../attributes';
import { Config } from '../../../video-data/config';
import type { CanvasTextDynamicCircleRendererSettings } from '../renderer/CanvasTextDynamicCircle';
import { CanvasTextDynamicCircleRenderer } from '../renderer/CanvasTextDynamicCircle';
import type { Pixels } from '../../../types';
import { int2rgba } from '../../../libs/videoData';
import {
    DynamicXAlign,
    DynamicYAlign
} from '../../../enums';
import { CanvasTextAssetDynamicGraphic } from './CanvasTextAssetDynamicGraphic';

/**
 * CanvasTextAssetDynamicCircle is the Luminary equivalent of CanvasTextDynamicCircle and provides
 * a bridge between Luminary and the CanvasText library.
 */
export class CanvasTextAssetDynamicCircle extends CanvasTextAssetDynamicGraphic {
    override _classType = 'CanvasTextDynamicCircle';

    override readonly type = Config.CIRCLE_GRAPHIC;
    @NumberAttribute({ min: 0, max: Infinity })
    radius = 0;
    @ColorAttribute()
    strokeColor = 0xFFFFFF;
    @AlphaAttribute()
    strokeAlpha = 1;
    @NumberAttribute({ min: 0, max: Infinity })
    strokeWidth = 0;

    override getCanvasRendererSettings(): CanvasTextDynamicCircleRendererSettings {
        const base = super.getCanvasRendererSettings();

        // make the stroke invisible for zero width
        const alpha = this.strokeWidth > 0 ? this.strokeAlpha : 0;
        const style = int2rgba(this.strokeColor, alpha);

        return {
            ...base,
            type: this.type,
            radius: this.radius * this.referenceToOutputMultiplier as Pixels,
            strokeStyle: style,
            lineWidth: this.strokeWidth * this.referenceToOutputMultiplier as Pixels,
        };
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize
            .concat(getAttributesToSerialize(this));
    }

    /* The list of properties that can be animated during draw */
    override get animatableProperties() {
        // Since CanvasTextDynamicCircle is responsible for rendering our text we need to ask it which properties it
        // supports. See its rendering logic for more details.
        return CanvasTextDynamicCircleRenderer.animatableProperties;
    }

    /** @inheritDoc */
    override get animatableGeometricProperties() {
        return CanvasTextDynamicCircleRenderer.animatableGeometricProperties;
    }

    override loadTextAssetStyle(settings) {
        // There was a bug in the old system where even though circles were drawn from the center, the alignment logic
        // was assuming they were drawn from the top left. This meant designers were adding a position offset to
        // compensate for this. We need to remove this offset for the canvas renderer since it aligns & draws circles
        // from the center.
        if (settings.alignGraphicX === DynamicXAlign.center) {
            settings.shape.positionX -= settings.shape.radius;
        }
        if (settings.alignGraphicY === DynamicYAlign.center) {
            settings.shape.positionY -= settings.shape.radius;
        }

        super.loadTextAssetStyle(settings);

        if (settings.shape.radius !== undefined) {
            this.radius = settings.shape.radius;
        }
        if (settings.shape.strokeColor !== undefined) {
            this.strokeColor = settings.shape.strokeColor;
        }
        if (settings.shape.strokeAlpha !== undefined) {
            this.strokeAlpha = settings.shape.strokeAlpha;
        }
        if (settings.shape.strokeWidth !== undefined) {
            this.strokeWidth = settings.shape.strokeWidth;
        }
    }
}
